import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/hooks/useAuth";
import { isAdmin as fetchIsAdmin } from "@/utils/roles.functions";
import { checkDomain } from "@/utils/dns.functions";
import {
  ShieldAlert,
  Globe,
  CheckCircle2,
  XCircle,
  Copy,
  RefreshCw,
  Lock,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin-domain")({
  head: () => ({
    meta: [{ title: "Domain — Admin — LendyTrack" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminDomain,
});

type CheckResult = Awaited<ReturnType<typeof checkDomain>>;

const BRAND_KEY = "lg_brand_settings";

type BrandSettings = { primaryDomain: string; brandName: string };

function loadBrand(): BrandSettings {
  if (typeof window === "undefined") return { primaryDomain: "", brandName: "LendyTrack" };
  try {
    const raw = localStorage.getItem(BRAND_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { primaryDomain: "", brandName: "LendyTrack" };
}

function saveBrand(b: BrandSettings) {
  localStorage.setItem(BRAND_KEY, JSON.stringify(b));
}

function AdminDomain() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [access, setAccess] = useState<"checking" | "allowed" | "denied">("checking");

  const [domain, setDomain] = useState("");
  const [result, setResult] = useState<CheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [brand, setBrand] = useState<BrandSettings>(loadBrand);
  const [currentHost, setCurrentHost] = useState("");

  useEffect(() => {
    setCurrentHost(typeof window !== "undefined" ? window.location.host : "");
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/auth" });
      return;
    }
    fetchIsAdmin()
      .then((r) => setAccess(r.isAdmin ? "allowed" : "denied"))
      .catch(() => setAccess("denied"));
  }, [user, authLoading, navigate]);

  async function runCheck(d?: string) {
    const target = (d ?? domain).trim().toLowerCase();
    if (!target) return;
    setChecking(true);
    setError("");
    try {
      const r = await checkDomain({ data: { domain: target } });
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Check failed");
      setResult(null);
    } finally {
      setChecking(false);
    }
  }

  function copy(text: string, label: string) {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed"),
    );
  }

  function copyZoneFile() {
    if (!result) return;
    const zone = [
      `; Lovable DNS template for ${result.apex}`,
      `@   3600 IN A   ${result.target.ip}`,
      `www 3600 IN A   ${result.target.ip}`,
      `_lovable 3600 IN TXT "lovable_verify=<paste-verify-token-from-Lovable>"`,
    ].join("\n");
    copy(zone, "DNS zone");
  }

  if (access !== "allowed") {
    return (
      <AppShell>
        <div className="mx-auto mt-20 max-w-md rounded-2xl border border-border bg-card p-10 text-center shadow-soft">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-danger/10 text-danger">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-display text-xl font-semibold">
            {access === "checking" ? "Verifying access…" : "Admins only"}
          </h1>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Domain & DNS</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Verify DNS propagation, SSL, and HTTPS enforcement for your custom domain.
          </p>
        </div>
        <Link
          to="/admin"
          className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
        >
          ← Admin
        </Link>
      </div>

      {/* Currently serving */}
      <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
          <Globe className="h-3 w-3" /> Currently serving
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className="font-display text-lg font-semibold">{currentHost || "—"}</span>
          {currentHost && (
            <button
              onClick={() => {
                setDomain(currentHost.replace(/^www\./, ""));
                runCheck(currentHost.replace(/^www\./, ""));
              }}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-semibold hover:bg-muted"
            >
              <RefreshCw className="h-3 w-3" /> Check this domain
            </button>
          )}
        </div>
      </div>

      {/* Checker */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-soft">
        <h2 className="font-display text-lg font-semibold">DNS propagation checker</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
            className="min-w-[260px] flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
            onKeyDown={(e) => e.key === "Enter" && runCheck()}
          />
          <button
            onClick={() => runCheck()}
            disabled={checking || !domain}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {checking ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {checking ? "Checking…" : "Check now"}
          </button>
        </div>
        {error && <p className="mt-3 text-sm text-danger">{error}</p>}

        {result && (
          <div className="mt-6 space-y-4">
            <StatusRow
              label="A record (root)"
              ok={result.records.apexA.ok}
              value={result.records.apexA.values.join(", ") || "no answer"}
              expected={result.target.ip}
            />
            <StatusRow
              label="A / CNAME (www)"
              ok={result.records.wwwA.ok}
              value={
                [
                  result.records.wwwA.values.join(", "),
                  result.records.wwwA.cname.length ? `CNAME → ${result.records.wwwA.cname.join(", ")}` : "",
                ]
                  .filter(Boolean)
                  .join(" • ") || "no answer"
              }
              expected={`A ${result.target.ip} or CNAME *.lovable.app`}
            />
            <StatusRow
              label="TXT verification"
              ok={result.records.txt.ok}
              value={result.records.txt.values.join(", ") || "no answer"}
              expected={`${result.target.txtName} = lovable_verify=…`}
            />

            <div className="grid gap-3 md:grid-cols-3">
              <SslCard
                label="HTTPS (root)"
                ok={result.https.apex.reachable && result.https.apex.sslValid}
                detail={
                  result.https.apex.reachable
                    ? `status ${result.https.apex.status} • SSL valid`
                    : result.https.apex.error ?? "unreachable"
                }
              />
              <SslCard
                label="HTTPS (www)"
                ok={result.https.www.reachable && result.https.www.sslValid}
                detail={
                  result.https.www.reachable
                    ? `status ${result.https.www.status} • SSL valid`
                    : result.https.www.error ?? "unreachable"
                }
              />
              <SslCard
                label="HTTP → HTTPS"
                ok={result.https.enforcesHttps}
                detail={result.https.enforcesHttps ? "Enforced (301/302 to https)" : "Not enforced"}
              />
            </div>

            <p className="text-xs text-muted-foreground">Checked {new Date(result.checkedAt).toLocaleString()}</p>
          </div>
        )}
      </div>

      {/* One-click DNS template */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-soft">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold">One-click DNS template</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Paste these into your registrar. Works for Cloudflare, Namecheap, GoDaddy, Route 53, etc.
            </p>
          </div>
          <button
            onClick={copyZoneFile}
            disabled={!result}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
          >
            <Copy className="h-4 w-4" /> Copy zone file
          </button>
        </div>
        <div className="mt-4 overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Value</th>
                <th className="px-4 py-2">TTL</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="font-mono text-xs">
              {[
                { type: "A", name: "@", value: "185.158.133.1", ttl: "3600" },
                { type: "A", name: "www", value: "185.158.133.1", ttl: "3600" },
                {
                  type: "TXT",
                  name: "_lovable",
                  value: "lovable_verify=<token-from-Lovable>",
                  ttl: "3600",
                },
              ].map((r) => (
                <tr key={r.name + r.type} className="border-t border-border">
                  <td className="px-4 py-2">{r.type}</td>
                  <td className="px-4 py-2">{r.name}</td>
                  <td className="px-4 py-2 break-all">{r.value}</td>
                  <td className="px-4 py-2">{r.ttl}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => copy(r.value, `${r.type} ${r.name}`)}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label="Copy"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Get your <code className="rounded bg-muted px-1">lovable_verify</code> token in Project Settings →
          Domains → Connect Domain.
        </p>
      </div>

      {/* HTTPS enforcement */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-soft">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
          <Lock className="h-3 w-3" /> HTTPS enforcement
        </div>
        <h2 className="mt-1 font-display text-lg font-semibold">All traffic forced to HTTPS</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The server redirects every <code className="rounded bg-muted px-1">http://</code> request to{" "}
          <code className="rounded bg-muted px-1">https://</code> with a 308 Permanent Redirect. SSL is
          auto-provisioned by Lovable via Let's Encrypt once DNS verifies.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 rounded-md border border-success/30 bg-success/10 px-3 py-1.5 text-xs font-semibold text-success">
          <CheckCircle2 className="h-3 w-3" /> Enforced in production
        </div>
      </div>

      {/* Domain-aware branding */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-soft">
        <h2 className="font-display text-lg font-semibold">Domain-aware branding</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          When the app loads under your custom domain, the brand name and canonical URL adapt automatically.
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <label className="block">
            <div className="mb-1 text-xs uppercase tracking-widest text-muted-foreground">Primary domain</div>
            <input
              value={brand.primaryDomain}
              onChange={(e) => setBrand({ ...brand, primaryDomain: e.target.value })}
              placeholder="lendytrack.ai"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block">
            <div className="mb-1 text-xs uppercase tracking-widest text-muted-foreground">Brand name</div>
            <input
              value={brand.brandName}
              onChange={(e) => setBrand({ ...brand, brandName: e.target.value })}
              placeholder="LendyTrack"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={() => {
              saveBrand(brand);
              toast.success("Branding saved");
            }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Save branding
          </button>
          {brand.primaryDomain && (
            <a
              href={`https://${brand.primaryDomain}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              Open live site <ArrowRight className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function StatusRow({
  label,
  ok,
  value,
  expected,
}: {
  label: string;
  ok: boolean;
  value: string;
  expected: string;
}) {
  return (
    <div className="flex flex-wrap items-start gap-3 rounded-lg border border-border bg-background p-3">
      <div className={`mt-0.5 grid h-6 w-6 place-items-center rounded-full ${ok ? "bg-success/15 text-success" : "bg-danger/15 text-danger"}`}>
        {ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="mt-0.5 break-all font-mono text-xs">{value}</div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">expected: {expected}</div>
      </div>
    </div>
  );
}

function SslCard({ label, ok, detail }: { label: string; ok: boolean; detail: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        {ok ? (
          <CheckCircle2 className="h-4 w-4 text-success" />
        ) : (
          <XCircle className="h-4 w-4 text-danger" />
        )}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{detail}</div>
    </div>
  );
}
